import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  Filler,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  ChartTooltip,
  ChartLegend,
  Filler,
);

const piePalette = ["#6C63FF", "#3B82F6", "#10B981", "#F59E0B", "#8B5CF6"];

const toRgba = (hex, alpha) => {
  const cleaned = hex.replace("#", "");
  const normalized =
    cleaned.length === 3
      ? cleaned
          .split("")
          .map((ch) => ch + ch)
          .join("")
      : cleaned;
  const intVal = parseInt(normalized, 16);
  const r = (intVal >> 16) & 255;
  const g = (intVal >> 8) & 255;
  const b = intVal & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

function PreviewChart({ preview }) {
  const commonOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#0f172a",
          titleColor: "#f8fafc",
          bodyColor: "#f8fafc",
          borderColor: "#1e293b",
          borderWidth: 1,
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: "#64748b", font: { size: 11 } },
        },
        y: {
          grid: { color: "#eef2f7" },
          ticks: { color: "#64748b", font: { size: 11 } },
          beginAtZero: true,
        },
      },
    }),
    [],
  );

  if (preview.chart === "line") {
    const data = {
      labels: preview.chartLabels,
      datasets: [
        {
          data: preview.chartData,
          borderColor: preview.chartColor,
          backgroundColor: toRgba(preview.chartColor, 0.12),
          fill: true,
          tension: 0.35,
          borderWidth: 2.5,
          pointRadius: 2,
          pointHoverRadius: 4,
        },
      ],
    };

    return (
      <div className="h-[200px]">
        <Line data={data} options={commonOptions} />
      </div>
    );
  }

  if (preview.chart === "bar") {
    const data = {
      labels: preview.chartLabels,
      datasets: [
        {
          data: preview.chartData,
          backgroundColor: preview.chartColor,
          borderRadius: 6,
          maxBarThickness: 34,
        },
      ],
    };

    return (
      <div className="h-[200px]">
        <Bar data={data} options={commonOptions} />
      </div>
    );
  }

  const data = {
    labels: preview.chartLabels,
    datasets: [
      {
        data: preview.chartData,
        backgroundColor: preview.chartLabels.map((_, i) => piePalette[i % piePalette.length]),
        borderWidth: 0,
        spacing: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "58%",
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          color: "#64748b",
          font: { size: 11 },
        },
      },
      tooltip: {
        backgroundColor: "#0f172a",
        titleColor: "#f8fafc",
        bodyColor: "#f8fafc",
      },
    },
  };

  return (
    <div className="h-[200px]">
      <Doughnut data={data} options={options} />
    </div>
  );
}

export default PreviewChart;
